/* Brand font for Shopify's Shop Pay Installments widget.
   ==========================================================================
   THE PROBLEM. <shopify-payment-terms> renders its text in GTStandard-M —
   Shop Pay's own brand font — which is the one piece of type on the page that
   is not our Helvetica.

   WHY CSS ALONE CANNOT DO IT. Measured in a real headless Chromium against the
   live preview, 2026-08-13, after a CSS-only attempt failed:

     host element   font-family -> "Helvetica Neue", Helvetica, Arial   (our CSS DID win)
     inner element  class="m-0 flex flex-col gap-2 font-sans"
                    font-family -> GTStandard-M, sans-serif             (unreachable)

   So the stylesheet in section CSS was working and still looked wrong: the
   component ships a Tailwind `.font-sans` utility on an element INSIDE its
   shadow root, which re-sets the font below whatever the host inherits. Outer
   CSS cannot select a shadow descendant, and the component exposes no
   `::part()` and reads no CSS custom properties — both verified by reading its
   bundle (`client.payment-terms_*.esm.js` plus its 24 chunks).

   WHAT WORKS. The shadow root is `mode: "open"`, so a stylesheet appended to it
   participates in the shadow cascade and can beat `.font-sans`. Verified end to
   end in the same browser session: inner font-family became our stack.

   WHY A <style> ELEMENT AND NOT adoptedStyleSheets. `adoptedStyleSheets` needs
   Safari 16.4+, and WebKit is ~85% of this storefront's sessions (Instagram's
   in-app browser alone is 44%, and on iPhone that is WKWebView). Appending a
   <style> node works on every browser that supports open shadow roots at all,
   so it is the wider net for the audience that actually visits.

   ⚠️ THIS REACHES INTO A THIRD-PARTY COMPONENT. It is deliberately the smallest
   possible intrusion — one font-family declaration, no layout, no colour, no
   content — but it is not a supported API. If Shopify renames `.font-sans`,
   closes the shadow root, or restructures the widget, this silently stops
   working and the widget returns to GTStandard-M. It CANNOT break the page or
   the payment: the failure mode is cosmetic and self-limiting. Re-check after
   any Shop Pay change.

   NOT DONE, deliberately: replacing the widget with our own text built from its
   `shopify-meta` attribute. That would put an instalment price on the page that
   no longer comes from the payment provider, and would lose the "Learn more"
   disclosure. Restyling Shopify's own component is the conservative option.
   ========================================================================== */
(function () {
	'use strict';

	var MARK = 'data-haki-terms-font';

	// The numbers live in CSS, not here. Each place that renders the widget sets
	// --hk-terms-* on its container (see haki-product.liquid and haki-cart.css),
	// this reads the computed values off the host and transports them across the
	// shadow boundary. So the PDP can match its own small grey caption while the
	// cart matches "Subtotal", and neither treatment is hard-coded in JavaScript
	// where nobody would think to look for it.
	// Type properties: safe to push onto every descendant. The Shop Pay logo is an
	// <svg> sized by its own utility classes, so font-size and friends do not move it.
	var TYPE = [
		['font-family', '--hk-terms-font', "'Helvetica Neue', Helvetica, Arial, sans-serif"],
		['font-size', '--hk-terms-size', '13px'],
		['font-weight', '--hk-terms-weight', '400'],
		['letter-spacing', '--hk-terms-ls', '-0.36px'],
		['text-transform', '--hk-terms-transform', 'none']
	];
	// Layout of the component's own flex container. `align` centres the block in
	// the PDP's column; `wrap` exists because the cart drawer is narrow and a
	// second line there costs real estate above the fold.
	var LAYOUT = [
		['align-items', '--hk-terms-align', 'flex-start'],
		['text-align', '--hk-terms-text-align', 'left'],
		['white-space', '--hk-terms-wrap', 'normal']
	];
	// COLOUR IS DELIBERATELY NOT PUSHED ONTO DESCENDANTS.
	// The Shop Pay logo's paths are fill="currentColor", and the component colours
	// that svg Shop Pay purple — measured as rgb(84, 51, 235). A `.font-sans *`
	// colour rule inherits straight into it and turns the brand mark grey, which
	// is what the first attempt at this did. Applying colour only to the host and
	// the text container lets the text take our colour while the svg keeps the one
	// the component set on it. Verified both ways in the browser.
	var COLOUR = ['color', '--hk-terms-color', '#111111'];

	function decl(cs, spec) {
		return spec[0] + ':' + (cs.getPropertyValue(spec[1]).trim() || spec[2]) + ' !important;';
	}

	function cssFor(host) {
		var cs = getComputedStyle(host);
		var type = '', layout = '';
		for (var i = 0; i < TYPE.length; i++) type += decl(cs, TYPE[i]);
		for (var j = 0; j < LAYOUT.length; j++) layout += decl(cs, LAYOUT[j]);
		// :host is the wrapper; .font-sans is the utility the component puts on its
		// text container; the descendant selector catches the price and "Learn more".
		// !important because .font-sans is a class and would otherwise win inside the
		// shadow tree.
		return ':host,.font-sans,.font-sans *{' + type + '}' +
			':host,.font-sans{' + decl(cs, COLOUR) + '}' +
			// The component lays its text out as `flex flex-col`, so alignment is an
			// align-items question, not just text-align. white-space rides here too:
			// it has to reach the <p>, which is where the wrapping actually happens.
			'.font-sans,.font-sans>*{' + layout + '}';
	}

	// KEEPING IT TO ONE LINE WITHOUT CLIPPING IT.
	//
	// The cart drawer is narrow and a second line there costs space above the
	// fold, so that container asks for `white-space: nowrap`. But nowrap alone is
	// a trap: Shopify's message gets LONGER above $150, where it swaps to
	// financing wording. Measured in the drawer at 452px wide, 11px bold:
	//
	//     $90   "Pay in 4 interest-free installments of $22.50..."   423px  fits
	//     $270  "4 interest-free installments, or from $24.37/mo..." 484px  OVERFLOWS
	//
	// So the size is chosen from the text that is actually there. Text width
	// scales close enough to linearly with font-size for one measured pass, with
	// a second to confirm. If even the floor will not fit — a longer message, a
	// narrower drawer, a language with longer words — wrapping is restored rather
	// than letting it clip. This is a payment disclosure: it may be small, it may
	// take two lines, it may never be cut off.
	var FIT = 'data-haki-terms-fit';

	function fitOneLine(host, root) {
		var cs = getComputedStyle(host);
		if (cs.getPropertyValue('--hk-terms-fit').trim() !== '1') return;
		var cont = root.querySelector('.font-sans');
		var p = cont && (cont.querySelector('p') || cont);
		if (!p || !cont.clientWidth) return;

		var start = parseFloat(cs.getPropertyValue('--hk-terms-size')) || 11;
		var min = parseFloat(cs.getPropertyValue('--hk-terms-fit-min')) || 9;
		var style = root.querySelector('style[' + FIT + ']');
		if (!style) {
			style = document.createElement('style');
			style.setAttribute(FIT, '');
			root.appendChild(style);
		}

		var size = start;
		for (var pass = 0; pass < 2; pass++) {
			style.textContent = '';                       // measure at the base size
			var avail = cont.clientWidth;
			var needed = p.scrollWidth;
			if (needed <= avail) { size = start; break; }
			size = Math.max(min, Math.floor((start * avail / needed) * 10) / 10);
			style.textContent = ':host,.font-sans,.font-sans *{font-size:' + size + 'px !important}';
			if (p.scrollWidth <= cont.clientWidth) break;
		}
		// Still too wide at the floor: let it wrap. Two readable lines beat one
		// truncated one.
		if (p.scrollWidth > cont.clientWidth) {
			style.textContent = ':host,.font-sans,.font-sans *{font-size:' + size +
				'px !important}.font-sans,.font-sans>*{white-space:normal !important}';
		}
	}

	function patch(host) {
		var root = host.shadowRoot;
		if (!root) return false;                              // not hydrated yet
		var existing = root.querySelector('style[' + MARK + ']');
		var css = cssFor(host);
		if (!existing) {
			existing = document.createElement('style');
			existing.setAttribute(MARK, '');
			root.appendChild(existing);
		}
		// The cart swaps in a fresh widget on every bag change, and the message
		// itself changes with the total, so both the styles and the fit are
		// re-evaluated rather than assumed to still hold.
		if (existing.textContent !== css) existing.textContent = css;
		if (window.requestAnimationFrame) {
			requestAnimationFrame(function () { fitOneLine(host, root); });
		} else {
			fitOneLine(host, root);
		}
		return true;
	}

	function scan() {
		var hosts = document.querySelectorAll('shopify-payment-terms');
		var pending = false;
		for (var i = 0; i < hosts.length; i++) {
			if (!patch(hosts[i])) pending = true;
		}
		return pending || hosts.length === 0;
	}

	// The widget hydrates asynchronously, and the cart drawer swaps in a fresh
	// copy whenever the bag total changes, so one pass at load is not enough.
	// Bounded polling rather than an unbounded interval: if the component never
	// arrives (Installments off, price out of range, script blocked) this stops
	// on its own instead of ticking for the life of the page.
	var tries = 0;
	var timer = setInterval(function () {
		var stillWaiting = scan();
		if (++tries >= 40 || !stillWaiting) clearInterval(timer);   // ~10s ceiling
	}, 250);
	scan();

	// New hosts can appear long after that ceiling — a cart refetch, a section
	// re-render in the theme editor. Watching for them is cheap and has no
	// deadline, where polling would have to.
	if (window.MutationObserver) {
		new MutationObserver(function (records) {
			for (var i = 0; i < records.length; i++) {
				if (records[i].addedNodes.length) { scan(); return; }
			}
		}).observe(document.documentElement, { childList: true, subtree: true });
	}
})();
